"use client"

import "./app-theme.css"
import "./builder-readable.css"
import "./builder-backgrounds.css"
import * as React from "react"
import { useAuthStore } from "@/stores/auth-store"
import { appThemeBootScript } from "@/lib/theme/app-themes/app-theme-boot"
import { enterAppShell, refreshAppThemeFromCookie } from "@/lib/theme/app-themes/app-theme-store"
import { useAppThemeAccountSync } from "@/lib/theme/app-themes/use-app-theme"
import { watchBuilderReadable } from "@/lib/theme/builder-readable-dom"
import { APP_INTER_FONT_CLASS } from "./app-fonts"

// Layout effects: <html> must be right before the browser paints. (The server renders only the script.)
const useIsomorphicLayoutEffect = typeof window === "undefined" ? React.useEffect : React.useLayoutEffect

const noSubscription = () => () => {}

/**
 * The customer app's themes, switched on for the page that renders this (spec
 * docs/superpowers/specs/2026-09-27-app-ready-made-themes-design.md, "Ruling 2"). The app sidebar
 * renders it, so it is mounted exactly while an app page is open — and only there: the play area, the
 * learner portal and the admin console never render the app sidebar.
 *
 *   - Server rendering: the first-paint script (lib/theme/app-themes/app-theme-boot.ts), which paints
 *     the mirrored theme on <html> while the page is still being parsed. It is rendered only on the
 *     server and during hydration; afterwards it is dropped. A script React creates on the client
 *     never runs, so a client-side arrival renders none and paints from the layout effect instead.
 *   - Mount / unmount: the page enters and leaves the theme store; the last app page to go takes the
 *     choice and the Inter class off <html>, so nothing follows the user to the play area or the portal.
 *   - The account: asked once per signed-in user; its answer is mirrored unless a save overtook it.
 *   - Another tab's choice: picked up when this tab comes back into focus or view.
 *
 *   - Where the builder's look is on (Custom, or someone who never chose a theme), the builder's colour
 *     made readable (lib/theme/builder-readable-dom.ts): the text shade, the ring and the ink on <body>
 *     while an app page is open, off again when it closes.
 *
 * It also brings the themes' stylesheet (./app-theme.css), whose every selector starts with
 * `html[data-app-theme` — so it matches nothing wherever the attribute is not — and the builder's
 * readability sheet (./builder-readable.css), which matches nothing without `data-builder-readable`, and the
 * builder's background tones (./builder-backgrounds.css), which match nothing without a tone's class on <body>
 * and then only in light mode, under Custom or for someone who never chose a theme.
 */
export function AppThemeScope() {
  const userId = useAuthStore((s) => s.user?.id ?? null)
  // True while React renders the server's markup (on the server, and during hydration); false after.
  const serverMarkup = React.useSyncExternalStore(noSubscription, () => false, () => true)

  useIsomorphicLayoutEffect(() => enterAppShell(APP_INTER_FONT_CLASS), [])
  // Under the builder's look, its colour made readable where it is text, rings and ink — app pages only.
  useIsomorphicLayoutEffect(() => watchBuilderReadable(), [])

  React.useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshAppThemeFromCookie()
    }
    window.addEventListener("focus", refreshAppThemeFromCookie)
    document.addEventListener("visibilitychange", onVisible)
    return () => {
      window.removeEventListener("focus", refreshAppThemeFromCookie)
      document.removeEventListener("visibilitychange", onVisible)
    }
  }, [])

  useAppThemeAccountSync(userId)

  if (!serverMarkup) return null
  return <script data-app-theme-boot="" dangerouslySetInnerHTML={{ __html: appThemeBootScript(APP_INTER_FONT_CLASS) }} />
}
